import { Box, CardMedia, CircularProgress, Popover, Typography } from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { Thumbnail } from '../Thumbnail';
import { getAssetFileName } from '@/utils/assets';
import classnames from 'classnames';
import { FindMatchesButton } from '../Matches/FindMatchesButton';
import { AddToCompareButton } from '../CompareCart/AddToCompareButton';
import { Ellipsis } from '../Ellipsis';
import { useTenants } from '@/services/tenants/useTenants';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { useEffect } from 'react';
import { Asset } from '@/api/generatedApi';

const DETAILS_HEIGHT = 180;

const useStyles = createUseStyles(theme => ({
  DependencyDetailPopover_paper: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    paddingRight: theme.spacing(15),
  },
  DependencyDetailPopover_title: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  DependencyDetailPopover_media: {
    height: DETAILS_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
  },
  DependencyDetailPopover_section: {
    display: 'flex',
    flexDirection: 'row',
    height: DETAILS_HEIGHT,
  },
  DependencyDetailPopover_details: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
    height: DETAILS_HEIGHT,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
  },
  DependencyDetailPopover_buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  DependencyDetailPopover_link: {
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  DependencyDetailPopover_loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
}));

const PopoverContent = ({ isLoading, asset, onClose }: { isLoading: boolean; asset?: Asset; onClose: () => void }) => {
  const c = useStyles();
  const { getPath } = useTenants();

  if (isLoading) {
    return (
      <Box className={c.DependencyDetailPopover_loading}>
        <CircularProgress />
      </Box>
    );
  }
  if (asset) {
    return (
      <Box className={c.DependencyDetailPopover_section}>
        <Link to={getPath(`asset/${asset.id}`)} onClick={onClose} className={c.DependencyDetailPopover_link}>
          <CardMedia className={c.DependencyDetailPopover_media}>
            <Thumbnail asset={asset} size={DETAILS_HEIGHT} noLink />
          </CardMedia>
        </Link>
        <Box className={c.DependencyDetailPopover_details}>
          <div>
            <Typography variant='subtitle1' className={c.DependencyDetailPopover_title}>
              Selected Part
            </Typography>
            <Link to={getPath(`asset/${asset.id}`)} onClick={onClose} className={c.DependencyDetailPopover_link}>
              <Ellipsis
                text={getAssetFileName(asset)}
                sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}
                tooltipProps={{ title: getAssetFileName(asset) }}
              />
            </Link>
          </div>

          <div className={c.DependencyDetailPopover_buttons}>
            <FindMatchesButton asset={asset} />
            <AddToCompareButton asset={asset} />
          </div>
        </Box>
      </Box>
    );
  }
  return;
};

export interface DependencyDetailPopoverProperties {
  open: boolean;
  anchorEl: HTMLElement | null;
  selectedTreeItemId: string;
  onClose: () => void;
  paperClassName?: string;
}

export const DependencyDetailPopover = ({
  open,
  anchorEl,
  selectedTreeItemId,
  onClose,
  paperClassName,
}: DependencyDetailPopoverProperties) => {
  const c = useStyles();
  const { showToast } = useToast();

  const { asset, isLoading, error } = useGetAsset({ assetId: selectedTreeItemId });

  useEffect(() => {
    if (error) {
      showToast({
        message: 'Failed to load asset details',
        type: 'error',
      });
      onClose();
    }
  }, [error, onClose, showToast]);

  if (error) {
    return;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onAbort={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      classes={{ paper: classnames(c.DependencyDetailPopover_paper, paperClassName) }}
      disableRestoreFocus
    >
      <PopoverContent isLoading={isLoading} asset={asset} onClose={onClose} />
    </Popover>
  );
};
